import { db } from '../instant';
import { id } from '@instantdb/react';
import { ItemCategory, ItemType } from '../schema';

/**
 * Check if a category can be edited (not system-defined)
 */
export function canEditCategory(category: ItemCategory): boolean {
    return !category.systemDefined;
}

/**
 * Get the count of items linked to a category
 */
export function getCategoryItemCount(categoryId: string, itemTypes: ItemType[]): number {
    return itemTypes.filter(item => item.category?.id === categoryId).length;
}

/**
 * Check if a category can be deleted
 * Returns { canDelete: boolean, reason?: string }
 */
export function canDeleteCategory(category: ItemCategory, itemTypes: ItemType[]): { canDelete: boolean; reason?: string } {
    if (category.systemDefined) {
        return { canDelete: false, reason: 'Cannot delete system category' };
    }

    const itemCount = getCategoryItemCount(category.id, itemTypes);
    if (itemCount > 0) {
        return { canDelete: false, reason: 'Cannot delete category with items' };
    }

    return { canDelete: true };
}

/**
 * Create a new item category
 */
export async function createCategory(params: { name: string; description?: string; color?: string; order: number }): Promise<string> {
    const categoryId = id();

    await db.transact(
        db.tx.itemCategories[categoryId].update({
            name: params.name,
            description: params.description,
            color: params.color,
            order: params.order,
            systemDefined: false,
        })
    );

    return categoryId;
}

/**
 * Update an item category
 * Throws error if category is system-defined
 */
export async function updateCategory(
    categoryId: string,
    category: ItemCategory,
    updates: {
        name?: string;
        description?: string;
        color?: string;
        order?: number;
    }
): Promise<void> {
    if (category.systemDefined) {
        throw new Error('Cannot edit system category');
    }

    await db.transact(db.tx.itemCategories[categoryId].update(updates));
}

/**
 * Delete an item category
 * Throws error if category is system-defined or has items
 */
export async function deleteCategory(category: ItemCategory, itemTypes: ItemType[]): Promise<void> {
    const deleteCheck = canDeleteCategory(category, itemTypes);
    if (!deleteCheck.canDelete) {
        throw new Error(deleteCheck.reason || 'Cannot delete category');
    }

    await db.transact(db.tx.itemCategories[category.id].delete());
}

/**
 * Reorder categories by updating their order field
 */
export async function reorderCategories(categoryIds: string[]): Promise<void> {
    const txs = categoryIds.map((categoryId, index) =>
        db.tx.itemCategories[categoryId].update({
            order: index + 1,
        })
    );

    await db.transact(txs);
}
